import { css } from "@emotion/react";
import { useCallback, useId, useReducer } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CloseIconOnDesktop from "@mui/icons-material/KeyboardDoubleArrowLeft";
import ExpandLessIcon from "@mui/icons-material/KeyboardArrowRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/Menu";
import OpenIconOnDesktop from "@mui/icons-material/KeyboardDoubleArrowRight";
import { useTheme } from "../theme";
import { useStateWithDeps } from "../hooks/useStateWithDeps";
import { useMatchMedia } from "../hooks/useMatchMedia";
import { TextButton, TextButtonProps } from "../TextButton";
import { Button } from "../Button";

export type GlobalMenuRenderInternalLink = (props: {
  href: string;
  children: React.ReactNode;
}) => React.JSX.Element;

interface MenuItem {
  text: string;
  href: string;
  count?: number;
}

interface MenuGroup {
  group: string;
  items: (MenuItem | MenuGroup)[];
}

const GlobalMenuItemCounter = ({ count }: { count: number }) => {
  const isOverHundred = count > 99;
  return count > 0 ? (
    <div
      css={[
        css`
          position: absolute;
          top: 4px;
          right: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          font-size: 12px;
          line-height: 1em;
          color: white;
          background-color: #f06687;
          border-radius: 50%;
        `,
        {
          fontSize: isOverHundred ? 10 : 12,
        },
      ]}
    >
      {isOverHundred ? "99+" : count}
    </div>
  ) : null;
};

interface GlobalMenuItemProps {
  count?: number;
  children?: React.ReactNode;
  href: string;
  renderInternalLink: GlobalMenuRenderInternalLink;
  onClick?: () => void;
}

const GlobalMenuItem = ({
  children,
  count,
  href,
  renderInternalLink: InternalLink,
  onClick,
}: GlobalMenuItemProps) => {
  const theme = useTheme();
  const { duration, easing } = theme.transitions;
  const isMobile = useMatchMedia(
    `(max-width: ${theme.breakpoints.sm}px)`,
    false,
  );
  return (
    <li
      css={css`
        position: relative;

        a {
          display: block;
          padding: 8px 0px 8px 28px;
          overflow: hidden;
          color: inherit;
          text-decoration: none;
          text-overflow: ellipsis;
          white-space: nowrap;
          transition: background-color ${duration.shortest} ${easing.sharp};
          &:hover,
          &[aria-current="page"] {
            background-color: #e8e8e8;
          }
        }
      `}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => {
          if (isMobile) {
            onClick?.();
          }
        }}
      >
        {href.startsWith("http") ? (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        ) : (
          <InternalLink href={href}>{children}</InternalLink>
        )}
        {count !== undefined ? <GlobalMenuItemCounter count={count} /> : null}
      </div>
    </li>
  );
};

interface GlobalMenuGroupProps extends MenuGroup {
  renderInternalLink: GlobalMenuRenderInternalLink;
  onClickItem?: () => void;
}

const GlobalMenuGroup = ({
  group,
  items,
  renderInternalLink,
  onClickItem,
}: GlobalMenuGroupProps) => {
  const theme = useTheme();
  const { duration, easing } = theme.transitions;
  const [open, toggleOpen] = useReducer((open) => !open, true);

  return (
    <li>
      <div
        role="button"
        tabIndex={0}
        onClick={toggleOpen}
        css={css`
          display: grid;
          grid-template-columns: 28px 1fr;
          padding: 8px 0px 8px 0px;
          transition: background-color ${duration.shortest} ${easing.sharp};
          &:hover {
            background-color: #e8e8e8;
          }
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
          `}
        >
          {open ? <ExpandMoreIcon /> : <ExpandLessIcon />}
        </div>
        {group}
      </div>
      {open ? (
        <ul
          css={css`
            padding: 0;
            padding-left: 12px;
            list-style: none;
          `}
        >
          {items.map((subItem) => {
            if ("group" in subItem) {
              throw new Error(
                "Nested group of more than 3 levels is not supported",
              );
            }
            return (
              <GlobalMenuItem
                href={subItem.href}
                key={subItem.text}
                count={subItem.count}
                renderInternalLink={renderInternalLink}
                onClick={onClickItem}
              >
                {subItem.text}
              </GlobalMenuItem>
            );
          })}
        </ul>
      ) : null}
    </li>
  );
};

const CustomTextIconButton = (props: TextButtonProps) => {
  return (
    <TextButton
      type="button"
      css={css`
        width: 40px;
        height: 40px;
        color: inherit;
        svg {
          font-size: 36px;
        }
        &:hover {
          background-color: #e8e8e8;
        }
      `}
      {...props}
    />
  );
};

export interface GlobalMenuProps {
  /**
   * 最初にメニューを開いておくかどうかを指定します。
   * ただし、モバイルでは指定に関わらず閉じられます。
   * @default true
   */
  defaultOpen?: boolean;
  items?: MenuGroup[];
  headerItems?: React.ReactNode;
  children?: React.ReactNode;
  logo?: React.ReactNode;
  renderInternalLink: (props: {
    href: string;
    children: React.ReactNode;
  }) => React.JSX.Element;
}

export const GlobalMenu = (props: GlobalMenuProps) => {
  const {
    children,
    logo,
    headerItems,
    items,
    renderInternalLink,
    defaultOpen,
  } = props;
  const theme = useTheme();
  const { duration, easing } = theme.transitions;
  const isMobile = useMatchMedia(
    `(max-width: ${theme.breakpoints.sm}px)`,
    false,
  );

  const [open, setOpen] = useStateWithDeps(() => {
    /**
     * モバイルでは常に閉じておく
     */
    if (isMobile) {
      return false;
    }
    /**
     * モバイルではなく、defaultOpenが指定されていなければ、常に開いておく
     */
    return defaultOpen ?? true;
  }, isMobile);

  const toggleOpen = useCallback(() => {
    setOpen((prev) => !prev);
  }, [setOpen]);

  const hasItems = items && items.length > 0;

  const menuId = useId();

  const sidebarId = useId();
  const controllsId = useId();

  return (
    <div
      css={css`
        --header-height: 72px;
        @media (max-width: ${theme.breakpoints.sm}px) {
          --header-height: 56px;
        }
      `}
    >
      <header
        css={css`
          position: sticky;
          top: 0px;
          z-index: ${theme.zIndex.appBar};
          display: flex;
          justify-content: space-between;
          height: var(--header-height);
          padding: 8px 32px;
          color: #555555;
          background: rgba(255, 255, 255, 0.6);
          backdrop-filter: blur(8.5px);
          @media (max-width: ${theme.breakpoints.sm}px) {
            padding: 8px 20px;
          }
        `}
      >
        <div
          css={css`
            align-self: center;
            @media (max-width: ${theme.breakpoints.sm}px) {
              display: none;
            }
          `}
        >
          {logo}
        </div>
        {hasItems ? (
          <div
            css={css`
              @media not (max-width: ${theme.breakpoints.sm}px) {
                display: none;
              }
            `}
          >
            <CustomTextIconButton onClick={toggleOpen} icon={<MenuIcon />} />
          </div>
        ) : (
          <div />
        )}
        <div
          css={css`
            display: flex;
            gap: 8px;
            align-items: center;
          `}
        >
          {headerItems}
        </div>
      </header>
      <div
        css={[
          hasItems
            ? css`
                --sidebar-width: 280px;
                display: grid;
                grid-template-columns: var(--sidebar-width) 1fr;
                &:has([id="${menuId}"][aria-hidden="true"]) {
                  --sidebar-width: 56px;
                }
                @media (max-width: ${theme.breakpoints.sm}px) {
                  --sidebar-width: 0px !important;
                  display: block;
                }
              `
            : css`
                --sidebar-width: 0px;
              `,
        ]}
      >
        {hasItems ? (
          <div
            css={css`
              @media (max-width: ${theme.breakpoints.sm}px) {
                display: none;
                &:has([id="${menuId}"][aria-hidden="false"]) {
                  display: block;
                }
              }
            `}
          >
            <aside
              css={[
                css`
                  position: fixed;
                  z-index: ${theme.zIndex.appBar};
                  will-change: transform;
                  @media (max-width: ${theme.breakpoints.sm}px) {
                    top: 0px;
                    z-index: ${theme.zIndex.modal};
                    width: 100%;
                  }
                `,
              ]}
              id={sidebarId}
            >
              <Button
                id={controllsId}
                aria-expanded={open}
                aria-controls={menuId}
                css={css`
                  position: fixed;
                  top: 6px;
                  right: 8px;
                  z-index: ${theme.zIndex.tooltip};
                  color: ${theme.palette.gray[800]};

                  svg {
                    color: inherit;
                  }

                  &[aria-expanded="true"] {
                    opacity: 0;
                    transition: opacity ${duration.shorter} ${easing.easeInOut};
                  }
                  [id="${sidebarId}"]:hover & {
                    opacity: 1;
                  }

                  @media (max-width: ${theme.breakpoints.sm}px) {
                    display: none;
                  }
                `}
                type="button"
                rounded
                icon={
                  open ? (
                    <CloseIconOnDesktop />
                  ) : (
                    <div
                      css={css`
                        display: flex;
                        align-items: center;
                        justify-content: center;
                      `}
                    >
                      <OpenIconOnDesktop
                        css={css`
                          display: none;
                          [id="${controllsId}"][aria-expanded="false"]:hover & {
                            display: block;
                          }
                        `}
                      />
                      <MenuIcon
                        css={css`
                          [id="${controllsId}"][aria-expanded="false"]:hover & {
                            display: none;
                          }
                        `}
                      />
                    </div>
                  )
                }
                onClick={toggleOpen}
              />
              <div
                css={css`
                  width: var(--sidebar-width);
                  height: calc(100vh - var(--header-height));
                  padding-block: 8px 56px;
                  padding-inline: 32px;
                  overflow-y: auto;
                  color: #767676;
                  background-color: #f3f3f3;
                  transition: width ${duration.shortest} ${easing.easeInOut};
                  @media (max-width: ${theme.breakpoints.sm}px) {
                    width: 100%;
                    height: 100vh;
                    height: 100dvh;
                    padding: 8px 20px 56px;
                    background-color: #ffffff;
                  }
                  @media not (max-width: ${theme.breakpoints.sm}px) {
                    transition: width ${duration.shortest} ${easing.easeInOut};
                    [id="${controllsId}"][aria-expanded="false"] + & {
                      padding-inline: 0px;
                    }
                  }
                `}
              >
                <div
                  css={css`
                    display: none;
                    @media (max-width: ${theme.breakpoints.sm}px) {
                      position: sticky;
                      top: 0px;
                      z-index: ${theme.zIndex.modal};
                      display: block;
                    }
                  `}
                >
                  <TextButton
                    css={css`
                      width: 40px;
                      height: 40px;
                      color: inherit;
                      svg {
                        font-size: 36px;
                      }
                      &:hover {
                        background-color: #e8e8e8;
                      }
                    `}
                    type="button"
                    icon={<CloseIcon />}
                    onClick={toggleOpen}
                  />
                </div>
                <div
                  css={css`
                    display: none;
                    @media (max-width: ${theme.breakpoints.sm}px) {
                      display: flex;
                      align-items: center;
                      justify-content: center;
                    }
                  `}
                >
                  {logo}
                </div>

                <nav
                  id={menuId}
                  aria-hidden={!open}
                  css={css`
                    @media not (max-width: ${theme.breakpoints.sm}px) {
                      &[aria-hidden="true"] {
                        display: none;
                      }
                    }
                  `}
                >
                  {items.map((groupItem) => {
                    return (
                      <div key={groupItem.group}>
                        <div
                          css={css`
                            padding: 40px 0px 16px 0px;
                            color: #939393;
                          `}
                        >
                          {groupItem.group}
                        </div>
                        <ul
                          css={[
                            theme.typography.body,
                            css`
                              padding: 0;
                              font-size: 16px;
                              list-style: none;
                            `,
                          ]}
                        >
                          {groupItem.items.map((item) => {
                            if ("group" in item) {
                              return (
                                <GlobalMenuGroup
                                  key={item.group}
                                  group={item.group}
                                  items={item.items}
                                  renderInternalLink={renderInternalLink}
                                  onClickItem={toggleOpen}
                                />
                              );
                            }
                            return (
                              <GlobalMenuItem
                                href={item.href}
                                key={item.text}
                                count={item.count}
                                renderInternalLink={renderInternalLink}
                                onClick={toggleOpen}
                              >
                                {item.text}
                              </GlobalMenuItem>
                            );
                          })}
                        </ul>
                      </div>
                    );
                  })}
                </nav>
              </div>
            </aside>
          </div>
        ) : null}
        <main
          css={css`
            min-height: calc(100vh - var(--header-height));
            overflow-x: auto;
          `}
        >
          {children}
        </main>
      </div>
    </div>
  );
};
